/**
 * Notify Me When Back In Stock — Claura
 * Same pattern as wishlist.js: config injected via window.NotifyMeConfig,
 * localStorage remembers name/phone for next time.
 */
(function () {
  'use strict';

  const cfg     = window.NotifyMeConfig || {};
  const API_URL = (cfg.apiUrl || '').replace(/\/$/, '');

  if (!API_URL) {
    console.warn('[NotifyMe] NotifyMeConfig.apiUrl is not set. Plugin disabled.');
    return;
  }

  const LS_NAME_KEY      = 'nm_name';
  const LS_PHONE_KEY     = 'nm_phone';
  const LS_SUBMITTED_KEY = 'nm_submitted'; // { [variant_id]: true }

  function getName()   { return localStorage.getItem(LS_NAME_KEY)  || ''; }
  function saveName(n) { localStorage.setItem(LS_NAME_KEY, n); }
  function getPhone()  { return localStorage.getItem(LS_PHONE_KEY) || ''; }
  function savePhone(p){ localStorage.setItem(LS_PHONE_KEY, p); }

  function getSubmitted() {
    try { return JSON.parse(localStorage.getItem(LS_SUBMITTED_KEY) || '{}'); }
    catch { return {}; }
  }
  function isSubmitted(variantId) {
    return !!getSubmitted()[String(variantId)];
  }
  function markSubmitted(variantId) {
    const submitted = getSubmitted();
    submitted[String(variantId)] = true;
    localStorage.setItem(LS_SUBMITTED_KEY, JSON.stringify(submitted));
  }

  // Applies the "already on the list" state to a single button based on
  // what's stored locally — called on load, after submit, and whenever the
  // theme re-renders the buy-buttons markup (variant switch, section reload).
  function applyButtonState(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('is-submitted')) return; // already applied — don't re-touch the DOM
    if (isSubmitted(variantId)) {
      btn.disabled = true;
      btn.classList.add('is-submitted');
      btn.textContent = "You're on the list";
    }
  }
  function applyAllButtonStates() {
    document.querySelectorAll('.nm-notify-btn').forEach(applyButtonState);
  }

  // ── API ──────────────────────────────────────────────────
  async function apiSubmit(payload) {
    const res = await fetch(`${API_URL}/api/notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // ── Modal state ──────────────────────────────────────────
  let pendingProductData = null;
  let pendingButton      = null;

  function overlay() { return document.getElementById('nm-modal-overlay'); }

  function openModal(btn, productData) {
    pendingProductData = productData;
    pendingButton      = btn;
    const ov = overlay();
    if (!ov) return;

    const nameEl = ov.querySelector('.nm-modal__product-name');
    const varEl  = ov.querySelector('.nm-modal__product-variant');
    const imgEl  = ov.querySelector('.nm-modal__product-img');

    if (nameEl) nameEl.textContent = productData.product_title || '';
    if (varEl)  varEl.textContent  = productData.variant_title || '';
    if (imgEl && productData.product_image) imgEl.src = productData.product_image;

    const nameInput  = ov.querySelector('#nm-name-input');
    const phoneInput = ov.querySelector('#nm-phone-input');
    if (nameInput  && getName())  nameInput.value  = getName();
    if (phoneInput && getPhone()) phoneInput.value = getPhone();

    showForm(ov);
    clearAllErrors(ov);
    ov.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (nameInput && !nameInput.value) setTimeout(() => nameInput.focus(), 250);
    else if (phoneInput) setTimeout(() => phoneInput.focus(), 250);
  }

  function closeModal() {
    const ov = overlay();
    if (!ov) return;
    ov.classList.remove('is-open');
    document.body.style.overflow = '';
    pendingProductData = null;
    pendingButton = null;
  }

  function showForm(ov) {
    ov.querySelector('.nm-modal__form-section').style.display = 'block';
    ov.querySelector('.nm-modal__success').classList.remove('is-visible');
  }
  function showSuccess(ov) {
    ov.querySelector('.nm-modal__form-section').style.display = 'none';
    ov.querySelector('.nm-modal__success').classList.add('is-visible');
    setTimeout(() => closeModal(), 2200);
  }

  // ── Errors ───────────────────────────────────────────────
  function showFieldError(ov, inputSel, errSel, msg) {
    const errEl = ov.querySelector(errSel);
    const input = ov.querySelector(inputSel);
    if (errEl) { errEl.textContent = msg; errEl.classList.add('is-visible'); }
    if (input) input.classList.add('has-error');
  }
  function clearFieldError(ov, inputSel, errSel) {
    const errEl = ov.querySelector(errSel);
    const input = ov.querySelector(inputSel);
    if (errEl) { errEl.textContent = ''; errEl.classList.remove('is-visible'); }
    if (input) input.classList.remove('has-error');
  }
  function clearAllErrors(ov) {
    clearFieldError(ov, '#nm-name-input',  '#nm-name-error');
    clearFieldError(ov, '#nm-phone-input', '#nm-phone-error');
  }

  // ── Validation ───────────────────────────────────────────
  function validatePhone(value) {
    const digits = value.trim().replace(/\D/g, '');
    if (digits.length === 10) return null;
    if (digits.length === 12 && digits.startsWith('91')) return null;
    return 'Please enter a valid 10-digit WhatsApp number.';
  }

  // ── Submit ───────────────────────────────────────────────
  async function submitModal() {
    const ov = overlay();
    if (!ov || !pendingProductData) return;

    const nameInput  = ov.querySelector('#nm-name-input');
    const phoneInput = ov.querySelector('#nm-phone-input');
    const btn        = ov.querySelector('#nm-modal-submit');

    const name  = nameInput.value.trim();
    const phone = phoneInput.value.trim();

    clearAllErrors(ov);
    let hasError = false;

    if (!name) {
      showFieldError(ov, '#nm-name-input', '#nm-name-error', 'Please enter your name.');
      hasError = true;
    }
    const phoneErr = validatePhone(phone);
    if (phoneErr) {
      showFieldError(ov, '#nm-phone-input', '#nm-phone-error', phoneErr);
      hasError = true;
    }
    if (hasError) return;

    btn.disabled = true;
    btn.textContent = 'Submitting...';

    try {
      const result = await apiSubmit({
        name,
        phone,
        variant_id:     pendingProductData.variant_id,
        variant_title:  pendingProductData.variant_title,
        product_id:     pendingProductData.product_id,
        product_title:  pendingProductData.product_title,
        product_handle: pendingProductData.product_handle,
        product_url:    pendingProductData.product_url,
      });

      savePhone(phone);
      saveName(name);
      markSubmitted(pendingProductData.variant_id);

      if (pendingButton) {
        pendingButton.disabled = true;
        pendingButton.classList.add('is-submitted');
        pendingButton.textContent = "You're on the list";
      }

      showSuccess(ov);
    } catch (e) {
      showFieldError(ov, '#nm-phone-input', '#nm-phone-error', 'Something went wrong. Please try again.');
      console.error('[NotifyMe] Submit failed:', e);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Notify Me';
    }
  }

  // ── Click handler for .nm-notify-btn ────────────────────
  function handleNotifyClick(btn) {
    const productData = {
      product_id:     btn.dataset.productId     || '',
      product_title:  btn.dataset.productTitle  || '',
      product_handle: btn.dataset.productHandle || '',
      product_url:    btn.dataset.productUrl    || window.location.href,
      variant_id:     btn.dataset.variantId      || '',
      variant_title:  btn.dataset.variantTitle   || '',
      product_image:  btn.dataset.productImage   || '',
    };
    openModal(btn, productData);
  }

  // ── Wiring ───────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const notifyBtn = e.target.closest('.nm-notify-btn');
    if (notifyBtn) {
      e.preventDefault();
      if (notifyBtn.disabled || notifyBtn.classList.contains('is-submitted')) return;
      handleNotifyClick(notifyBtn);
      return;
    }

    const ov = overlay();
    if (!ov) return;

    if (e.target.closest('.nm-modal__close') || e.target === ov) {
      closeModal();
      return;
    }

    if (e.target.closest('#nm-modal-submit')) {
      submitModal();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  // ── Persisted state on load + after variant switches ─────
  // NOTE: a previous version of this used a MutationObserver on
  // document.body (childList + subtree), watching every DOM change on the
  // whole page. On PDPs with other scripts that mutate the DOM frequently
  // (quantity-break progress bars, carousels, trackers, etc.) that caused a
  // runaway loop that crashed the Safari tab ("A problem repeatedly
  // occurred"). Use the theme's own bubbling `variant:change` event instead
  // — it fires only when the variant actually swaps, and by the time it
  // fires the theme has already replaced the buy-buttons markup.
  applyAllButtonStates();

  document.addEventListener('variant:change', function () {
    // Swap has already happened synchronously by this point, but defer to
    // the next tick in case any listener still needs to finish updating
    // the DOM first.
    setTimeout(applyAllButtonStates, 0);
  });
})();